import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { getMonthlyRewardPromotionConfig } from '../../services/promotionConfigService'
import type { MonthlyRewardTabKey } from '../../services/monthlyRewardService'
import { getEnabledMonthlyRewardTabs, isMonthlyRewardConfigUpdate } from '../../utils/monthlyRewardTabs'

function getActiveTab(pathname: string): MonthlyRewardTabKey | null {
  const match = pathname.match(/\/reward\/([^/]+)/)
  const tab = match?.[1]
  if (tab === 'cashback' || tab === 'loss' || tab === 'refer' || tab === 'rank') {
    return tab
  }
  return null
}

const ClassicRewardLayout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [tabSettings, setTabSettings] = useState<Partial<Record<MonthlyRewardTabKey, { enabled?: boolean; title?: string }>>>({})
  const [moduleEnabled, setModuleEnabled] = useState(true)
  const [disabledTitle, setDisabledTitle] = useState('ปิดใช้งานชั่วคราว')
  const [disabledMessage, setDisabledMessage] = useState('ระบบรางวัลประจำเดือนปิดใช้งานชั่วคราว')
  const [loadingTabs, setLoadingTabs] = useState(true)

  const loadTabSettings = useCallback(async () => {
    try {
      const response = await getMonthlyRewardPromotionConfig()
      if (response.success && response.data) {
        setModuleEnabled(response.data.enabled !== false)
        const display = response.data.settings?.display
        if (typeof display?.disabledTitle === 'string' && display.disabledTitle.trim()) {
          setDisabledTitle(display.disabledTitle)
        }
        if (typeof display?.disabledMessage === 'string' && display.disabledMessage.trim()) {
          setDisabledMessage(display.disabledMessage)
        }
        if (response.data.settings?.tabs) {
          setTabSettings(response.data.settings.tabs)
        }
      }
    } catch {
      setTabSettings({})
      setModuleEnabled(true)
    } finally {
      setLoadingTabs(false)
    }
  }, [])

  useEffect(() => {
    void loadTabSettings()
  }, [loadTabSettings])

  useEffect(() => {
    const handleRewardRefresh = (event: Event) => {
      const detail = (event as CustomEvent)?.detail
      if (!isMonthlyRewardConfigUpdate(detail)) return
      void loadTabSettings()
    }

    window.addEventListener('content_update', handleRewardRefresh as EventListener)
    window.addEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)

    return () => {
      window.removeEventListener('content_update', handleRewardRefresh as EventListener)
      window.removeEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)
    }
  }, [loadTabSettings])

  const enabledTabs = useMemo(() => getEnabledMonthlyRewardTabs(tabSettings), [tabSettings])
  const activeTab = getActiveTab(location.pathname)

  useEffect(() => {
    if (loadingTabs || enabledTabs.length === 0) return
    if (!activeTab || !enabledTabs.some((tab) => tab.key === activeTab)) {
      navigate(`/reward/${enabledTabs[0].to}`, { replace: true })
    }
  }, [activeTab, enabledTabs, loadingTabs, navigate])

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-trophy me-2" />
                      รางวัลประจำเดือน
                    </div>

                    {!loadingTabs && (!moduleEnabled || enabledTabs.length === 0) ? (
                      <div className="bdr-5 bgc-4 p-3 text-center c-dark">
                        <div className="fw-bold mb-2">{disabledTitle}</div>
                        <div className="small mb-0">{disabledMessage}</div>
                      </div>
                    ) : (
                      <>
                        <div className="row mx--1 mb-2">
                          <div className="col-12 px-1">
                            <div className="nav nav-pills nav-pills-ticket mx--1" role="tablist">
                              {enabledTabs.map((tab) => (
                                <div
                                  key={tab.key}
                                  className={`nav-link col px-1 py-0 ${activeTab === tab.key ? 'active' : ''}`}
                                >
                                  <NavLink
                                    to={`/reward/${tab.to}`}
                                    className={({ isActive }) =>
                                      `check-button w-100 d-block border-0 text-decoration-none ${
                                        isActive ? 'bgg-5 c-white' : 'bgc-4 c-dark'
                                      }`
                                    }
                                  >
                                    <p className="mb-0 py-2">{tab.label}</p>
                                  </NavLink>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="col-12 px-1">
                          {loadingTabs ? (
                            <div className="bdr-5 bgc-4 p-3 text-center c-dark">กำลังโหลด...</div>
                          ) : (
                            <Outlet />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClassicRewardLayout
